// ============================================================================
// Gmail — error classes + wire shapes + sanitiser helpers.
//
// Gmail API errors follow a standard Google shape:
//   { error: { code: 400, message: '...', errors: [{ reason, domain, message }],
//              status: 'INVALID_ARGUMENT' } }
//
// We classify the most common failure modes into a discriminated error class
// so callers (dispatcher, chat inbox, agents) can react without re-parsing
// Google's documented HTTP status / `errors[].reason` matrix:
//
//   - AUTH_FAILED            → access token rejected (401 / invalid_grant).
//                                Caller must refresh the token.
//   - TOKEN_EXPIRED          → refresh token rejected (invalid_grant from
//                                the OAuth token endpoint). Instance needs
//                                re-auth by a human.
//   - RATE_LIMITED           → 429 or `rateLimitExceeded`. Retry with backoff.
//   - MESSAGE_NOT_FOUND      → 404 / `notFound`.
//   - ATTACHMENT_TOO_LARGE   → 413 / `sizeLimitExceeded`.
//   - QUOTA_EXCEEDED         → `dailyLimitExceeded` / quota exhausted for
//                                the day.
//   - PROVIDER_ERROR         → everything else (5xx, unknown shape).
//   - INVALID_PAYLOAD        → 400 (not token / size / reply related).
//   - HISTORY_EXPIRED        → `historyNotFound` — `history.list` start id
//                                fell outside Gmail's retention window; a
//                                full resync via `messages.list` is required.
// ============================================================================

// ============================================================================
// Wire shapes — the minimum slice of the Gmail API schema we care about.
// ============================================================================

/** Google error envelope — shared across most GCP APIs. */
export interface GoogleErrorEnvelope {
  error?: GoogleError
}

export interface GoogleError {
  code?: number
  message?: string
  status?: string
  errors?: ReadonlyArray<GoogleErrorDetail>
}

export interface GoogleErrorDetail {
  message?: string
  domain?: string
  /** `rateLimitExceeded`, `dailyLimitExceeded`, `notFound`, … */
  reason?: string
  location?: string
  locationType?: string
}

/** `users.messages.send` response. */
export interface GmailSendResponse {
  id: string
  threadId: string
  labelIds?: string[]
}

/** `users.messages.list` response. */
export interface GmailMessagesListResponse {
  messages?: Array<{ id: string; threadId: string }>
  nextPageToken?: string
  resultSizeEstimate?: number
}

/** Header entry inside a `users.messages.get` payload. */
export interface GmailHeader {
  name: string
  value: string
}

/** A MIME part inside a Gmail message (`payload` / recursive). */
export interface GmailMessagePart {
  partId?: string
  mimeType?: string
  filename?: string
  headers?: GmailHeader[]
  body?: {
    size?: number
    data?: string
    /** For attachment parts: the opaque id used by `attachments.get`. */
    attachmentId?: string
  }
  parts?: GmailMessagePart[]
}

/** `users.messages.get?format=full` response. */
export interface GmailMessage {
  id: string
  threadId: string
  labelIds?: string[]
  snippet?: string
  /** Milliseconds since epoch, as a string. */
  internalDate?: string
  historyId?: string
  payload?: GmailMessagePart
  /** Present only when `format=raw` is requested. */
  raw?: string
  sizeEstimate?: number
}

/** `users.messages.attachments.get` response. */
export interface GmailAttachmentPayload {
  size?: number
  /** base64url-encoded. */
  data?: string
}

/** `users.watch` response. */
export interface GmailWatchResponse {
  historyId: string
  /** ISO milliseconds — returned as a string per Google's convention. */
  expiration: string
}

// ============================================================================
// GmailError — discriminated by `code` so callers can switch exhaustively.
// ============================================================================

export type GmailErrorCode =
  | 'AUTH_FAILED'
  | 'TOKEN_EXPIRED'
  | 'RATE_LIMITED'
  | 'MESSAGE_NOT_FOUND'
  | 'ATTACHMENT_TOO_LARGE'
  | 'QUOTA_EXCEEDED'
  | 'PROVIDER_ERROR'
  | 'INVALID_PAYLOAD'
  | 'HISTORY_EXPIRED'
  | 'INVALID_INPUT'

export interface GmailErrorDetails {
  /** HTTP status of the underlying request, if known. */
  httpStatus?: number
  /** Raw provider message (sanitised). */
  providerMessage?: string
  /** First provider-reported reason (e.g. `rateLimitExceeded`). */
  providerReason?: string
  /** Parsed Retry-After (ms) — only populated for RATE_LIMITED. */
  retryAfterMs?: number
  /** Echoed detail entries from the envelope (sanitised). */
  providerDetails?: Array<{ message?: string; reason?: string; domain?: string }>
}

export class GmailError extends Error {
  public readonly name = 'GmailError'
  constructor(
    public readonly code: GmailErrorCode,
    message: string,
    public readonly details: GmailErrorDetails = {},
  ) {
    super(message)
  }
}

/** Dedicated error for Pub/Sub push authentication failures — distinct class
 *  so webhook tests can match on `instanceof` without reaching for a string
 *  code. */
export class GmailSignatureError extends Error {
  public readonly name = 'GmailSignatureError'
  constructor(message: string) {
    super(message)
  }
}

// ============================================================================
// Helpers — classify / detect / sanitise
// ============================================================================

/**
 * Parse a `Retry-After` header value (either delta-seconds or HTTP-date) and
 * return milliseconds. Returns `undefined` on unparseable input so callers
 * can fall back to a default backoff strategy.
 */
export function parseRetryAfterMs(headerValue: string | null | undefined): number | undefined {
  if (!headerValue) return undefined
  const trimmed = headerValue.trim()
  if (trimmed.length === 0) return undefined
  const asNumber = Number(trimmed)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return Math.round(asNumber * 1000)
  }
  const parsed = Date.parse(trimmed)
  if (Number.isFinite(parsed)) {
    const diff = parsed - Date.now()
    return diff > 0 ? diff : 0
  }
  return undefined
}

/**
 * Map a Google error envelope + HTTP status to a `GmailError`.
 *
 * Decision tree (ordered — first match wins):
 *   1. reason `invalid_grant`                        → TOKEN_EXPIRED
 *   2. HTTP 401                                      → AUTH_FAILED
 *   3. reason `rateLimitExceeded` OR HTTP 429        → RATE_LIMITED
 *   4. reason `dailyLimitExceeded` / `quotaExceeded` → QUOTA_EXCEEDED
 *   5. reason `historyNotFound`                      → HISTORY_EXPIRED
 *   6. reason `notFound` OR HTTP 404                 → MESSAGE_NOT_FOUND
 *   7. reason `sizeLimitExceeded` OR HTTP 413        → ATTACHMENT_TOO_LARGE
 *   8. HTTP 400                                      → INVALID_PAYLOAD
 *   9. everything else                               → PROVIDER_ERROR
 *
 * `providerCode` is the legacy arg name — we accept it but internally look at
 * `envelope.error.errors[0].reason` which is the canonical Gmail reason key.
 */
export function classifyGmailError(
  httpStatus: number,
  providerCode: string | undefined,
  providerError: GoogleErrorEnvelope | null | undefined,
  retryAfterMs?: number,
): GmailError {
  const err = providerError?.error
  const firstReason =
    (Array.isArray(err?.errors) && err!.errors!.length > 0 && typeof err!.errors![0]?.reason === 'string'
      ? err!.errors![0]!.reason
      : providerCode) || ''
  const rawMessage = err?.message ?? `Gmail API error (HTTP ${httpStatus})`
  const message = sanitizeGmailErrorMessage(rawMessage)

  const details: GmailErrorDetails = {
    httpStatus,
    providerMessage: message,
    providerReason: firstReason || undefined,
    retryAfterMs,
    providerDetails: Array.isArray(err?.errors)
      ? err!.errors!.slice(0, 8).map((d) => ({
          message: d?.message ? sanitizeGmailErrorMessage(d.message) : undefined,
          reason: d?.reason,
          domain: d?.domain,
        }))
      : undefined,
  }

  // 1. invalid_grant — refresh token revoked / user re-consented / client rotated.
  if (firstReason === 'invalid_grant' || /invalid_grant/i.test(rawMessage)) {
    return new GmailError('TOKEN_EXPIRED', message, details)
  }
  // 2. Raw 401 from Gmail API (access token expired / revoked).
  if (httpStatus === 401) {
    return new GmailError('AUTH_FAILED', message, details)
  }
  // 3. Rate-limit signals — reason first, HTTP second.
  if (firstReason === 'rateLimitExceeded' || httpStatus === 429) {
    return new GmailError('RATE_LIMITED', message, details)
  }
  // 4. Daily quota or broader quota family.
  if (firstReason === 'dailyLimitExceeded' || firstReason === 'quotaExceeded') {
    return new GmailError('QUOTA_EXCEEDED', message, details)
  }
  // 5. Gmail-specific history expiration — requires full resync.
  if (firstReason === 'historyNotFound') {
    return new GmailError('HISTORY_EXPIRED', message, details)
  }
  // 6. Not found — reason or 404.
  if (firstReason === 'notFound' || httpStatus === 404) {
    return new GmailError('MESSAGE_NOT_FOUND', message, details)
  }
  // 7. Payload too large.
  if (firstReason === 'sizeLimitExceeded' || httpStatus === 413) {
    return new GmailError('ATTACHMENT_TOO_LARGE', message, details)
  }
  // 8. Generic 400 → invalid payload (after the specific cases above).
  if (httpStatus === 400) {
    return new GmailError('INVALID_PAYLOAD', message, details)
  }
  return new GmailError('PROVIDER_ERROR', message, details)
}

// ============================================================================
// Secret sanitisation
// ============================================================================

/**
 * Strip Gmail / Google-shaped secrets out of an error / log string before it
 * leaves the adapter. Patterns mirror `scrubProviderError` in
 * `apps/api/src/services/ai-agent-processor.ts` and add Gmail-specific shapes:
 *
 *   - `access_token=...` / `refresh_token=...` query-string leaks
 *   - `Bearer <token>` headers
 *   - `AIza...` — Google API keys
 *   - `GOCSPX-...` — Google OAuth client secrets
 *   - `eyJ...` — bare JWTs (id tokens, signed bodies)
 *   - Long opaque base64url blobs (40+ chars) that look like access tokens
 *
 * Patterns intentionally redact aggressively — false positives on an error
 * string are cheap; a leaked token in Workers tail logs is catastrophic.
 */
export function sanitizeGmailErrorMessage(raw: string): string {
  if (!raw) return raw
  let out = raw
  // Query-string style token leaks.
  out = out.replace(/(access[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(refresh[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(id[_-]?token=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(client[_-]?secret=)[^&\s"']+/gi, '$1REDACTED')
  out = out.replace(/(code_verifier=)[^&\s"']+/gi, '$1REDACTED')
  // `Authorization: Bearer <token>` / `Bearer <token>`.
  out = out.replace(/(Bearer\s+)[A-Za-z0-9._~+/=\-|]+/gi, '$1REDACTED')
  // Google API keys.
  out = out.replace(/AIza[A-Za-z0-9_\-]{8,}/g, 'AIza***')
  // Google OAuth client secrets.
  out = out.replace(/GOCSPX-[A-Za-z0-9_\-]{8,}/g, 'GOCSPX-***')
  // Bare JWTs anywhere in the message.
  out = out.replace(
    /eyJ[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{10,}\.[A-Za-z0-9_\-]{8,}/g,
    'eyJ***',
  )
  // Anecdotal: Google SDK dumps sometimes include "Google-Assistant" tokens;
  // redact the shape `Google-Assistant <token>` defensively.
  out = out.replace(/(Google-Assistant\s+)[A-Za-z0-9._~+/=\-|]{8,}/gi, '$1REDACTED')
  // Long opaque base64url blobs (40+ chars). Anchored to avoid chewing up
  // legitimate hex ids / threadIds (Gmail thread ids are hex ≤16 chars, so
  // the 40-char floor is safe).
  out = out.replace(
    /[A-Za-z0-9+/_\-]{40,}={0,2}/g,
    (match) => {
      // Keep pure hex (SHA / UUID-ish) for debugging.
      if (/^[0-9a-f]+$/.test(match) && match.length <= 64) return match
      return 'REDACTED'
    },
  )
  return out
}
